import { FC, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@kizen/kds/Icon';
import { Typography } from '@kizen/kds/Typography';
import type { SourceFile } from 'ts-components/PiChat/protocol';
import {
  buildFileTree,
  commonDirPrefix,
  defaultFilePath,
  toQaIdSegment,
  visibleFiles,
  type FileTreeNode,
  type TreeFile,
} from './tree';
import { countLines, highlightSource } from './highlight';
import { ScrollFades } from 'ts-components/PiChat/ScrollFades';
import {
  Chevron,
  CodeBlock,
  CodePaneRoot,
  CodeScroll,
  CodeViewerHeader,
  DisclosureButton,
  FilesSection,
  LineGutter,
  PaneFrame,
  SplitPane,
  TreeList,
  TreePane,
  TreeRoot,
  TreeRowButton,
  TreeRowLabel,
  TreeScroll,
} from './styles';
import { SkeletonBarInline, SkeletonInline } from 'ts-components/PiChat/styles';

interface TreeNodesProps {
  qaPrefix: string;
  nodes: FileTreeNode[];
  depth: number;
  collapsed: ReadonlySet<string>;
  selectedPath: string | null;
  onToggleFolder: (displayPath: string) => void;
  onSelectFile: (path: string) => void;
}

const TreeNodes: FC<TreeNodesProps> = ({
  qaPrefix,
  nodes,
  depth,
  collapsed,
  selectedPath,
  onToggleFolder,
  onSelectFile,
}) => (
  <TreeList>
    {nodes.map((node) => {
      if (node.kind === 'file') {
        const selected = node.path === selectedPath;
        return (
          <li key={`file:${node.path}`}>
            <TreeRowButton
              type="button"
              $depth={depth}
              $selected={selected}
              aria-pressed={selected}
              title={node.displayPath}
              onClick={() => onSelectFile(node.path)}
              data-qa-id={`${qaPrefix}-file-${toQaIdSegment(node.displayPath)}`}
            >
              <Icon
                icon="file-lines"
                size="sm"
                color={
                  selected ? 'button-secondary-default' : 'font-placeholder'
                }
              />
              <TreeRowLabel>{node.name}</TreeRowLabel>
            </TreeRowButton>
          </li>
        );
      }
      const expanded = !collapsed.has(node.displayPath);
      return (
        <li key={`folder:${node.displayPath}`}>
          <TreeRowButton
            type="button"
            $depth={depth}
            title={node.displayPath}
            aria-expanded={expanded}
            onClick={() => onToggleFolder(node.displayPath)}
            data-qa-id={`${qaPrefix}-folder-${toQaIdSegment(node.displayPath)}`}
          >
            <Icon
              icon={expanded ? 'file-folder-open' : 'file-folder-closed'}
              size="sm"
              color="font-placeholder"
            />
            <TreeRowLabel>{node.name}</TreeRowLabel>
          </TreeRowButton>
          {expanded ? (
            <TreeNodes
              qaPrefix={qaPrefix}
              nodes={node.children}
              depth={depth + 1}
              collapsed={collapsed}
              selectedPath={selectedPath}
              onToggleFolder={onToggleFolder}
              onSelectFile={onSelectFile}
            />
          ) : null}
        </li>
      );
    })}
  </TreeList>
);

const CodePane: FC<{ qaPrefix: string; file: SourceFile }> = ({
  qaPrefix,
  file,
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const html = useMemo(
    () => highlightSource(file.path, file.content),
    [file.path, file.content]
  );
  const lineNumbers = useMemo(
    () =>
      Array.from({ length: countLines(file.content) }, (_, i) => i + 1).join(
        '\n'
      ),
    [file.content]
  );
  return (
    <CodePaneRoot>
      <CodeViewerHeader title={file.path}>
        {file.path.split('/').pop()}
      </CodeViewerHeader>
      <PaneFrame>
        <ScrollFades
          scrollRef={scrollRef}
          deps={[file.path, file.content]}
          horizontal
          color="var(--color-code-highlight-surface)"
        />
        <CodeScroll
          ref={scrollRef}
          className="kds-expanding-scrollbar"
          data-qa-id={`${qaPrefix}-file-viewer`}
        >
          <LineGutter aria-hidden>{lineNumbers}</LineGutter>
          <CodeBlock>
            <code className="hljs" dangerouslySetInnerHTML={{ __html: html }} />
          </CodeBlock>
        </CodeScroll>
      </PaneFrame>
    </CodePaneRoot>
  );
};

export interface FileTreeProps {
  qaPrefix: string;
  files: TreeFile[] | null;
  isLoading?: boolean;
  loadingRevealed?: boolean;
  defaultOpen?: boolean;
}

export const FileTree: FC<FileTreeProps> = ({
  qaPrefix,
  files,
  isLoading = false,
  loadingRevealed = false,
  defaultOpen = true,
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(defaultOpen);
  const [collapsed, setCollapsed] = useState<ReadonlySet<string>>(
    () => new Set()
  );
  const [selectedPath, setSelectedPath] = useState<string | null>(null);
  const treeScrollRef = useRef<HTMLDivElement>(null);

  const shown = useMemo(() => visibleFiles(files ?? []), [files]);
  const tree = useMemo(() => buildFileTree(shown), [shown]);
  const rootName = useMemo(
    () =>
      commonDirPrefix(shown.map((file) => file.path))
        .split('/')
        .filter(Boolean)
        .pop() ?? null,
    [shown]
  );
  const selectedFile =
    shown.find((file) => file.path === selectedPath) ??
    shown.find((file) => file.path === defaultFilePath(tree)) ??
    null;
  const fileCount = shown.length;
  const hasFiles = fileCount > 0;

  const handleToggleFolder = (displayPath: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(displayPath)) next.delete(displayPath);
      else next.add(displayPath);
      return next;
    });

  const expanded = open && hasFiles;

  return (
    <FilesSection>
      <DisclosureButton
        type="button"
        aria-expanded={expanded}
        disabled={!hasFiles}
        onClick={() => setOpen((prev) => !prev)}
        data-qa-id={`${qaPrefix}-files-toggle`}
      >
        <Chevron $expanded={expanded} aria-hidden />
        <Typography weight="bold" size="sm">
          {t('Files')}
        </Typography>
        {hasFiles || !isLoading ? (
          <Typography size="sm" color="font-placeholder">
            {hasFiles ? fileCount : t('None yet')}
          </Typography>
        ) : (
          <SkeletonInline
            $revealed={loadingRevealed}
            aria-hidden
            data-qa-id={`${qaPrefix}-files-loading`}
          >
            <SkeletonBarInline $width="48px" $height={10} />
          </SkeletonInline>
        )}
      </DisclosureButton>
      {expanded ? (
        <SplitPane>
          <TreePane>
            <PaneFrame>
              <ScrollFades
                scrollRef={treeScrollRef}
                deps={[fileCount, collapsed]}
              />
              <TreeScroll
                ref={treeScrollRef}
                className="kds-expanding-scrollbar"
              >
                {rootName ? (
                  <TreeRoot title={rootName}>
                    <Icon
                      icon="file-folder-open"
                      size="sm"
                      color="font-placeholder"
                    />
                    <TreeRowLabel>{rootName}</TreeRowLabel>
                  </TreeRoot>
                ) : null}
                <TreeNodes
                  qaPrefix={qaPrefix}
                  nodes={tree}
                  depth={rootName ? 1 : 0}
                  collapsed={collapsed}
                  selectedPath={selectedFile?.path ?? null}
                  onToggleFolder={handleToggleFolder}
                  onSelectFile={setSelectedPath}
                />
              </TreeScroll>
            </PaneFrame>
          </TreePane>
          {selectedFile?.content !== undefined ? (
            <CodePane qaPrefix={qaPrefix} file={selectedFile} />
          ) : null}
        </SplitPane>
      ) : null}
    </FilesSection>
  );
};
